class CartDrawer extends HTMLElement {
  constructor() {
    super();

    // ✅ Variável para guardar posição do scroll
    this.scrollPosition = 0;
    this._scrollbarResetTimeout = null;

    this.addEventListener('keyup', (evt) => evt.code === 'Escape' && this.close());
    const overlay = this.querySelector('#CartDrawer-Overlay');
    if (overlay && !overlay.__cdOverlayBound) {
      overlay.addEventListener('click', this.close.bind(this));
      overlay.__cdOverlayBound = true;
    }
    this.setHeaderCartIconAccessibility();
  }

  setHeaderCartIconAccessibility() {
    const cartLink = document.querySelector('#cart-icon-bubble');
    if (!cartLink) return;
    
    cartLink.setAttribute('role', 'button');
    cartLink.setAttribute('aria-haspopup', 'dialog');
    cartLink.addEventListener('click', (event) => {
      event.preventDefault();
      this.open(cartLink);
    });
    cartLink.addEventListener('keydown', (event) => {
      if (event.code.toUpperCase() === 'SPACE') {
        event.preventDefault();
        this.open(cartLink);
      }
    });
  }

  open(triggeredBy) {
    if (triggeredBy) this.setActiveElement(triggeredBy);
    const cartDrawerNote = this.querySelector('[id^="Details-"] summary');
    if (cartDrawerNote && !cartDrawerNote.hasAttribute('role')) this.setSummaryAccessibility(cartDrawerNote);

    if (this._scrollbarResetTimeout) {
      clearTimeout(this._scrollbarResetTimeout);
      this._scrollbarResetTimeout = null;
    }

    // Dawn cart.js seta #CartDrawer-CartErrors no .catch mas nunca limpa em sucesso.
    // Limpar a cada abertura captura todos os fluxos (add, change, remove, upsell).
    const errDiv = this.querySelector('#CartDrawer-CartErrors') || document.getElementById('cart-errors');
    if (errDiv) errDiv.textContent = '';
    this.querySelectorAll('.cart-item__error-text').forEach((el) => (el.innerHTML = ''));

    // ✅ SALVAR POSIÇÃO DO SCROLL ANTES DE TUDO
    this.scrollPosition = window.pageYOffset || document.documentElement.scrollTop;
    
    // ✅ CORREÇÃO SCROLLBAR
    const scrollbarWidth = window.innerWidth - document.documentElement.clientWidth;
    document.documentElement.style.setProperty('--scrollbar-width', scrollbarWidth + 'px');
    
    // ✅ Aplicar estilos para travar o scroll mantendo a posição.
    // position:fixed no body QUEBRA position:sticky (galeria do PDP,
    // .product__column-sticky, só existe em >=750px), fazendo a imagem
    // "subir" e abrir um buraco. Por isso só usamos fixed em mobile (<750px),
    // onde a galeria não é sticky e o iOS realmente precisa do fixed.
    // No desktop, overflow:hidden (classe .overflow-hidden + regra :has)
    // já trava o fundo E preserva o sticky.
    if (window.matchMedia('(max-width: 749px)').matches) {
      document.body.style.position = 'fixed';
      document.body.style.top = `-${this.scrollPosition}px`;
      document.body.style.left = '0';
      document.body.style.right = '0';
    }
    document.body.style.paddingRight = `${scrollbarWidth}px`;
    
    document.documentElement.classList.add('overflow-hidden');
    // NÃO adicionar 'overflow-hidden' no body em desktop: base.css tem a regra
    // genérica `.overflow-hidden { overflow: hidden }`, e overflow:hidden em
    // html E body ao mesmo tempo quebra o position:sticky da galeria do PDP
    // (imagem "sobe" ~188px). Em mobile (<750px) mantemos no body porque lá
    // a galeria não é sticky e o lock combina com o position:fixed do iOS.
    if (window.matchMedia('(max-width: 749px)').matches) {
      document.body.classList.add('overflow-hidden');
    }
    
    setTimeout(() => {
      this.classList.add('animate', 'active');
      const cartDrawerItems = this.querySelector('cart-drawer-items');
      if (cartDrawerItems) {
        if (typeof cartDrawerItems.updateCartUpsellToggleState === 'function') {
          cartDrawerItems.updateCartUpsellToggleState();
        }
        if (typeof cartDrawerItems.updateCartUpsellVisibility === 'function') {
          cartDrawerItems.updateCartUpsellVisibility();
        }
      }
    });

    let focusTrapped = false;
    const trapHandler = () => {
      if (focusTrapped) return;
      focusTrapped = true;
        const containerToTrapFocusOn = this.classList.contains('is-empty')
          ? this.querySelector('.drawer__inner-empty')
          : document.getElementById('CartDrawer');
        const focusElement = this.querySelector('.drawer__inner') || this.querySelector('.drawer__close');
        trapFocus(containerToTrapFocusOn, focusElement);
    };
    this.addEventListener('transitionend', trapHandler, { once: true });
    setTimeout(trapHandler, 600);
  }

  close() {
    this.classList.remove('active');
    removeTrapFocus(this.activeElement);
    
    // ✅ Remover classes
    document.documentElement.classList.remove('overflow-hidden');
    document.body.classList.remove('overflow-hidden');
    
    // ✅ Remover estilos inline do body
    document.body.style.position = '';
    document.body.style.top = '';
    document.body.style.left = '';
    document.body.style.right = '';
    document.body.style.paddingRight = '';
    
    // ✅ RESTAURAR POSIÇÃO DO SCROLL
    window.scrollTo(0, this.scrollPosition);
    
    // ✅ Resetar scrollbar width após transição (cancelável se reabrir antes)
    this._scrollbarResetTimeout = setTimeout(() => {
      document.documentElement.style.setProperty('--scrollbar-width', '0px');
      this._scrollbarResetTimeout = null;
    }, 300);
  }

  setSummaryAccessibility(cartDrawerNote) {
    cartDrawerNote.setAttribute('role', 'button');
    cartDrawerNote.setAttribute('aria-expanded', 'false');

    if (cartDrawerNote.nextElementSibling.getAttribute('id')) {
      cartDrawerNote.setAttribute('aria-controls', cartDrawerNote.nextElementSibling.id);
    }

    cartDrawerNote.addEventListener('click', (event) => {
      event.currentTarget.setAttribute('aria-expanded', !event.currentTarget.closest('details').hasAttribute('open'));
    });

    cartDrawerNote.parentElement.addEventListener('keyup', onKeyUpEscape);
  }

  renderContents(parsedState) {
    const innerEl = this.querySelector('.drawer__inner');
    if (innerEl && innerEl.classList.contains('is-empty')) innerEl.classList.remove('is-empty');
    this.productId = parsedState.id;
    this.getSectionsToRender().forEach((section) => {
      const sectionElement = section.selector
        ? document.querySelector(section.selector)
        : document.getElementById(section.id);
      const html = parsedState.sections && parsedState.sections[section.id];
      if (!sectionElement || !html) return;
      const parsed = new DOMParser().parseFromString(html, 'text/html');
      const usedSelector = section.selector || '.shopify-section';
      const inner = parsed.querySelector(usedSelector);
      if (!inner) return;
      sectionElement.innerHTML = inner.innerHTML;
    });

    setTimeout(() => {
      const overlay = this.querySelector('#CartDrawer-Overlay');
      if (overlay && !overlay.__cdOverlayBound) {
        overlay.addEventListener('click', this.close.bind(this));
        overlay.__cdOverlayBound = true;
      }
      this.open();
    });
  }

  getSectionInnerHTML(html, selector = '.shopify-section') {
    const node = new DOMParser().parseFromString(html, 'text/html').querySelector(selector);
    return node ? node.innerHTML : null;
  }

  getSectionsToRender() {
    return [
      {
        id: 'cart-drawer',
        selector: '#CartDrawer',
      },
      {
        id: 'cart-icon-bubble',
      },
    ];
  }

  getSectionDOM(html, selector = '.shopify-section') {
    return new DOMParser().parseFromString(html, 'text/html').querySelector(selector);
  }

  setActiveElement(element) {
    this.activeElement = element;
  }
}

customElements.define('cart-drawer', CartDrawer);

class CartDrawerItems extends CartItems {
  getSectionsToRender() {
    return [
      {
        id: 'CartDrawer',
        section: 'cart-drawer',
        selector: '.drawer__inner',
      },
      {
        id: 'cart-icon-bubble',
        section: 'cart-icon-bubble',
        selector: '.shopify-section',
      },
    ];
  }
}

customElements.define('cart-drawer-items', CartDrawerItems);